import React, { useEffect, useId, useState } from 'react';
import { FlexyFormLabel } from '@europrocurement/flexy-components';
import { Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import {
    Autocomplete,
    Box,
    Chip,
    FormHelperText,
    lighten,
    ListItem,
    SxProps,
    TextField,
    useTheme,
} from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { AutocompleteChangeReason, AutocompleteChangeDetails } from '@mui/material/Autocomplete';
import AutocompleteListbox from './AutocompleteListbox';
import AutocompleteOption from './AutocompleteOption';

/**
 * Props for the ControlledAutocomplete component
 *
 * @typedef {Object} ControlledAutocompleteProps
 * @property {string} name - The name of the form field, used by react-hook-form
 * @property {RegisterOptions} rules - Validation rules for the field (e.g., required, min length)
 * @property {string} inputlabel - The label text displayed above the input field
 * @property {boolean} [required] - Whether the field is required (displays in label)
 * @property {string} [placeholder] - Placeholder text shown in the input field
 * @property {string} [listboxTitle] - Title displayed above the dropdown list
 * @property {Function} getOptionValue - Function to extract a unique identifier from an option object
 * @property {Function} getOptionLabel - Function to extract the display label from an option object
 * @property {Function} onSearch - Async function that takes a search term and returns a promise of matching options
 * @property {Function} [isOnChangeValid] - Optional function to validate the selected values and/or modify them before saving
 * @property {boolean} [freeSolo=false] - Whether users can enter custom values not in the options list
 * @property {SxProps} [sx] - Material-UI sx prop for custom styling
 */
export type ControlledAutocompleteProps = {
    name: string;
    rules: RegisterOptions;
    inputlabel: string;
    required?: boolean;
    placeholder?: string;
    listboxTitle?: string;
    getOptionValue: (option: unknown) => number | string;
    getOptionLabel: (option: unknown) => React.ReactNode;
    onSearch: (term: string) => Promise<Array<unknown>>;
    isOnChangeValid?: (
        value: Array<unknown>,
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<unknown>,
    ) => { valid: boolean; validValue: unknown[] };
    freeSolo?: boolean;
    sx?: SxProps;
};

const ControlledAutocomplete: React.FunctionComponent<ControlledAutocompleteProps> = function ({
    name,
    rules,
    inputlabel,
    required,
    placeholder,
    listboxTitle,
    getOptionValue,
    getOptionLabel,
    onSearch,
    isOnChangeValid,
    freeSolo = false,
    sx,
}) {
    const { control } = useFormContext();
    const id = useId();
    const [options, setOptions] = useState<Array<unknown>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const { palette } = useTheme();

    const refreshOptions = (term: string) => {
        setLoading(true);
        onSearch(term)
            .then((items) => {
                setOptions(items);
            })
            .catch(() => {
                setOptions([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        refreshOptions('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                    {inputlabel && inputlabel !== '' ? (
                        <FlexyFormLabel
                            data-testid="test-id-FlexyInput-label"
                            required={required}
                        >
                            {inputlabel}
                        </FlexyFormLabel>
                    ) : null}
                    <Autocomplete
                        multiple
                        id={id}
                        options={options}
                        value={value}
                        disableCloseOnSelect
                        loading={loading}
                        getOptionLabel={() => ''}
                        getOptionKey={getOptionValue}
                        freeSolo={freeSolo}
                        clearIcon={false}
                        noOptionsText="Votre recherche ne correspond à aucun résultat."
                        loadingText="Recherche en cours"
                        popupIcon={null}
                        filterOptions={(opts) => opts}
                        isOptionEqualToValue={(a: unknown, b: unknown) =>
                            getOptionValue(a) === getOptionValue(b)
                        }
                        onInputChange={(_event, inputValue) => refreshOptions(inputValue)}
                        onChange={(_event, newValue, reason, details) => {
                            if (typeof isOnChangeValid !== 'undefined') {
                                const { valid, validValue } = isOnChangeValid(
                                    newValue,
                                    reason,
                                    details,
                                );
                                if (valid) {
                                    onChange(validValue);
                                }
                            } else {
                                onChange(newValue);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder={value && value.length > 0 ? '' : placeholder}
                            />
                        )}
                        renderOption={(props, option, state) => {
                            // eslint-disable-next-line react/prop-types
                            const { key, ...otherProps } = props;
                            const itemProps = {
                                ...otherProps,
                                color: 'primary.main',
                            };

                            return (
                                <ListItem
                                    {...itemProps}
                                    key={key}
                                >
                                    <AutocompleteOption
                                        selected={state?.selected || false}
                                        option={option}
                                        getOptionLabel={getOptionLabel}
                                    />
                                </ListItem>
                            );
                        }}
                        ListboxComponent={AutocompleteListbox}
                        ListboxProps={{
                            title: listboxTitle,
                            sx: { padding: '10px' },
                        }}
                        renderTags={(tags, getTagProps) =>
                            tags.map((tag, tagIndex) => {
                                const baseTagProps = { ...getTagProps({ index: tagIndex }) };
                                const tagProps = {
                                    ...baseTagProps,
                                    label: getOptionLabel(tag),
                                    sx: {
                                        backgroundColor: 'primary.light',
                                        paddingRight: '6px',
                                        fontWeight: '500',
                                        fontSize: '14px',
                                        color: lighten(palette.secondary.main, 0.3),
                                    },
                                    deleteIcon: (
                                        <Box onClick={baseTagProps.onDelete}>
                                            <FaOptionIcon
                                                sx={{
                                                    lineHeight: 0,
                                                    color: 'primary.dark',
                                                    fontSize: '10px',
                                                    padding: 0,
                                                }}
                                                icon={['fas', 'close']}
                                            />
                                        </Box>
                                    ),
                                };

                                return <Chip {...tagProps} />;
                            })
                        }
                        sx={sx}
                    />
                    {error ? (
                        <FormHelperText
                            sx={{
                                margin: '4px 14px 0px 14px',
                            }}
                            error
                        >
                            {error?.message}
                        </FormHelperText>
                    ) : null}
                </>
            )}
        />
    );
};

export default ControlledAutocomplete;
